import { useAtom } from "@dbeining/react-atom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, Outlet, useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import { userAtom } from "../../atoms";
import { useViewport } from "../../contexts";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ParentEntityLifecycleBanner, isSiteScopedFormReadOnly } from "../../components/Layout/ParentEntityLifecycleBanner";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import type { ICustomerDetail, ICustomerMainLocation } from "../../customTypings/customer/Customer";
import { customersService } from "../../services/customers/customers.service";
import {
    buildRecordLifecycleConfirmProps,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    customerLocationsPageTitle,
    customerNewLocationPageTitle,
    getUserFacingErrorMessage,
    ListPageTitles,
    loadingPageTitle,
    logErrorForDev,
    SectionLabels,
    showToastSuccess,
} from "../../utils";
import {
    canMutateCustomers,
    getCustomerAdminActionSuccessMessage,
    type CustomerAdminConfirmKind,
} from "../CustomersListPage/customerAdminListActions";
import {
    buildCustomerEditHeaderActions,
    customerEditHeaderNavLabel,
    customerEditHeaderNavPath,
    type CustomerEditActiveView,
} from "./customerEditHeaderActions";
import type { CustomerEditOutletContext } from "./customerEditOutletContext";
import { formatCustomerMetaByLine, formatCustomerMetaDate } from "./customerEditMetaFormatters";

type ConfirmKind = CustomerAdminConfirmKind | null;

const SITE_STORAGE_KEY = "CustomersListPage.siteId";

type CustomerEditLocationState = { siteId?: string };

const CustomerEditLayout = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const navigationSiteId = (location.state as CustomerEditLocationState | null)?.siteId ?? "";
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateCustomers(roleNames);

    const isLocationsRoute = Boolean(useMatch("/customers/:id/locations"));
    const isNewLocationRoute = Boolean(useMatch("/customers/:id/new-location"));
    const activeView: CustomerEditActiveView = isNewLocationRoute
        ? "newLocation"
        : isLocationsRoute
          ? "locations"
          : "edit";

    const [siteId, setSiteId] = useState(navigationSiteId);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [customerName, setCustomerName] = useState("");
    const [mainLocation, setMainLocation] = useState<ICustomerMainLocation | null>(null);
    const [siteName, setSiteName] = useState("");
    const [metaEnabled, setMetaEnabled] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [siteIsEnabled, setSiteIsEnabled] = useState(true);
    const [siteIsDeleted, setSiteIsDeleted] = useState(false);
    const [siteAccessLevel, setSiteAccessLevel] = useState<'readWrite' | 'readOnly' | 'none'>('readWrite');
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);

    const applyCustomerDetail = useCallback((customer: ICustomerDetail) => {
        setSiteId(customer.siteId);
        setSiteName(customer.siteName ?? "");
        if (typeof localStorage !== "undefined") {
            localStorage.setItem(SITE_STORAGE_KEY, customer.siteId);
        }
        setCustomerName(customer.name);
        setMainLocation(customer.mainLocation ?? null);
        setMetaEnabled(customer.isEnabled);
        setMetaDeleted(customer.isDeleted);
        setSiteIsEnabled(customer.siteIsEnabled);
        setSiteIsDeleted(customer.siteIsDeleted);
        setSiteAccessLevel(customer.siteAccessLevel ?? 'readWrite');
        setCreatedAt(customer.createdAtUtc);
        setUpdatedAt(customer.updatedAtUtc);
        setCreatedByDisplayName(customer.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(customer.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(customer.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(customer.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(customer.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(customer.disabledByDisplayName ?? null);
    }, []);

    useEffect(() => {
        if (!id) return;
        const loadSiteId =
            navigationSiteId ||
            siteId ||
            (typeof localStorage !== "undefined" ? localStorage.getItem(SITE_STORAGE_KEY) : null) ||
            "";
        if (!loadSiteId) {
            setLoadError("Site context is missing. Open this customer from the Customers list.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadError(null);
        customersService
            .getById(loadSiteId, id)
            .then(applyCustomerDetail)
            .catch((e: unknown) => {
                logErrorForDev("CustomerEditLayout.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load customer."));
            })
            .finally(() => setLoading(false));
    }, [id, navigationSiteId, applyCustomerDetail]);

    const prevActiveViewRef = useRef(activeView);
    useEffect(() => {
        if (!id || !siteId) return;
        const previousView = prevActiveViewRef.current;
        prevActiveViewRef.current = activeView;
        if (activeView === "edit" && previousView === "locations") {
            customersService
                .getById(siteId, id)
                .then(applyCustomerDetail)
                .catch((e: unknown) => logErrorForDev("CustomerEditLayout.refreshMainLocation", e));
        }
    }, [activeView, id, siteId, applyCustomerDetail]);

    const headerMenuItem = useMemo(
        () => ({ isEnabled: metaEnabled, isDeleted: metaDeleted }),
        [metaEnabled, metaDeleted]
    );

    const readOnlyForm = isSiteScopedFormReadOnly(metaDeleted, siteAccessLevel);
    const lifecycleHeaderFilter = useMemo(
        () => ({ readOnlyForm, siteIsDeleted, metaDeleted }),
        [readOnlyForm, siteIsDeleted, metaDeleted]
    );

    const headerMenuActions = useMemo(() => {
        if (!id) return [];
        return buildCustomerEditHeaderActions(
            navigate,
            id,
            setConfirmKind,
            activeView,
            lifecycleHeaderFilter
        );
    }, [navigate, id, activeView, lifecycleHeaderFilter]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.customer,
                customerName || `this ${EntityLabels.customer.toLowerCase()}`
            ),
        [confirmKind, customerName]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || !kind || !siteId) return;
        const op =
            kind === "disable"
                ? customersService.disable(siteId, id)
                : kind === "enable"
                  ? customersService.enable(siteId, id)
                  : kind === "softDelete"
                    ? customersService.softDelete(siteId, id)
                    : customersService.restore(siteId, id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getCustomerAdminActionSuccessMessage(kind, customerName));
            if (kind === "softDelete") {
                navigate("/customers", { replace: true });
                return;
            }
            return customersService.getById(siteId, id).then(applyCustomerDetail);
        }).catch(() => setConfirmKind(null));
    };

    const outletContext = useMemo((): CustomerEditOutletContext | undefined => {
        if (!id || !siteId) return undefined;
        return {
            siteId,
            customerId: id,
            customerName,
            mainLocation,
            metaDeleted,
            metaEnabled,
            siteIsEnabled,
            siteIsDeleted,
            siteAccessLevel,
            readOnlyForm,
            setConfirmKind,
        };
    }, [
        id,
        siteId,
        customerName,
        mainLocation,
        metaDeleted,
        metaEnabled,
        siteIsEnabled,
        siteIsDeleted,
        siteAccessLevel,
        readOnlyForm,
    ]);

    const pageTitle =
        activeView === "locations"
            ? customerLocationsPageTitle(EntityLabels.customer)
            : activeView === "newLocation"
              ? customerNewLocationPageTitle(EntityLabels.customer)
              : editPageTitle(EntityLabels.customer, false);

    const breadcrumbItems = [
        { text: SectionLabels.operations, to: "/operations" },
        { text: ListPageTitles.customers, to: "/customers" },
        { text: editFormBreadcrumbLabel(false), to: `/customers/${id}` },
        ...(activeView === "locations" ? [{ text: "Locations" as const }] : []),
        ...(activeView === "newLocation" ? [{ text: "Locations" as const, to: `/customers/${id}/locations` }, { text: "New Location" as const }] : []),
    ];

    if (!canMutate) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage customers.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.customer)} />
            </Container>
        );
    }

    if (loadError || !id || !siteId) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError ?? "Customer not found."}</Alert>
                <Link to="/customers">Back to customers</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <ResponsiveBreadcrumb className="mb-3" items={breadcrumbItems} />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {pageTitle}
                </h1>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    {!isNarrowScreen && (
                        <Button
                            variant="secondary"
                            onClick={() => navigate(customerEditHeaderNavPath(activeView, id))}
                        >
                            {customerEditHeaderNavLabel(activeView)}
                        </Button>
                    )}
                    {isNarrowScreen && (
                        <EllipsisActionMenu
                            id="customer-edit-actions-dropdown"
                            ariaLabel="Customer actions"
                            actions={headerMenuActions}
                            item={headerMenuItem}
                        />
                    )}
                </div>
            </div>

            <RecordAuditMetaBar
                formatDate={formatCustomerMetaDate}
                formatByLine={formatCustomerMetaByLine}
                createdAt={createdAt}
                createdByDisplayName={createdByDisplayName}
                updatedAt={updatedAt}
                lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                isDeleted={metaDeleted}
                isEnabled={metaEnabled}
                deletedAuditAtUtc={deletedAuditAtUtc}
                deletedByDisplayName={deletedByDisplayName}
                disabledAuditAtUtc={disabledAuditAtUtc}
                disabledByDisplayName={disabledByDisplayName}
            />

            <ParentEntityLifecycleBanner
                entityLabel={EntityLabels.customer}
                name={customerName}
                entityIsDeleted={metaDeleted}
                entityIsEnabled={metaEnabled}
                parentSiteName={siteName}
                parentSiteIsDeleted={siteIsDeleted}
                parentSiteIsEnabled={siteIsEnabled}
                accessLevel={siteAccessLevel}
            />

            {outletContext && <Outlet context={outletContext} />}

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { CustomerEditLayout };
